import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductCard from '../components/ProductCard';
import { api } from '../lib/api';

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        const products = await api.getProducts(null, true);
        setFeaturedProducts(products.slice(0, 6));
      } catch (error) {
        console.error('Error fetching featured products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedProducts();
  }, []);

  return (
    <div className="paper-texture">
      {/* Hero Section */}
      <section 
        className="hero-section flex items-center justify-center relative overflow-hidden"
        style={{
          backgroundImage: 'url(/images/hero-background.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-deep-berry/60"></div>
        <div className="relative z-10 text-center text-white max-w-4xl px-6">
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mb-6 text-white" data-testid="hero-title">
            Cape Cod's Finest
            <br />
            <span className="text-gold">Artisan Preserves</span>
          </h1>
          <p className="text-lg sm:text-xl mb-8 leading-relaxed max-w-2xl mx-auto">
            Small batch jams, jellies, and marmalades crafted with care in Osterville, Massachusetts
          </p>
          <Link to="/shop">
            <Button className="btn-primary inline-flex items-center space-x-2 text-base" data-testid="shop-now-button">
              <span>Shop Now</span>
              <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl lg:text-5xl mb-4" data-testid="featured-section-title">
              Featured Selections
            </h2>
            <p className="text-lg text-[#5C5855] max-w-2xl mx-auto">
              Handpicked favorites from our artisan collection
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-cape-red border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="featured-products-grid">
              {featuredProducts.map((product) => (
                <ProductCard key={product.sku} product={product} />
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/shop">
              <Button className="btn-secondary" data-testid="view-all-button">
                View All Products
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-alt-bg">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-4xl lg:text-5xl mb-6">
                Small Batch,
                <br />
                Big Flavor
              </h2>
              <p className="text-lg text-[#5C5855] mb-6 leading-relaxed">
                Since our founding, we've been crafting exceptional preserves using traditional methods and the finest ingredients. Each jar represents our commitment to quality and the rich culinary heritage of Cape Cod.
              </p>
              <p className="text-lg text-[#5C5855] mb-8 leading-relaxed">
                From our classic beach plum jam to innovative flavor combinations, every preserve is made in small batches to ensure the highest quality and most vibrant flavors.
              </p>
              <Link to="/about">
                <Button className="btn-primary">
                  Our Story
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1613463455809-28608760827d?w=600&h=600&fit=crop"
                alt="Artisan preserves"
                className="w-full h-full object-cover soft-shadow"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-8 soft-shadow">
                <p className="handwritten text-3xl text-deep-berry">Handcrafted with love</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-display text-4xl lg:text-5xl text-center mb-16">
            Shop by Category
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {['Jellies', 'Jams', 'Marmalades', 'Pepper Jellies', 'Preserves', 'Chutneys'].map((category) => (
              <Link
                key={category}
                to={`/shop?category=${category}`}
                className="group"
                data-testid={`category-${category.toLowerCase().replace(' ', '-')}`}
              >
                <div className="bg-white border-2 border-alt-bg hover:border-cape-red p-6 text-center transition-all duration-300 group-hover:shadow-lg">
                  <h3 className="font-display text-lg text-deep-berry group-hover:text-cape-red transition-colors">
                    {category}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
